import type { NwiSourceAcreageConflict, NwiSourceAttributeVariant } from '../../types/refdata';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';

const sha = /^[0-9a-f]{64}$/;
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const finiteAcres = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;

export type NwiAcreageQualification =
  | { status: 'ordinary' }
  | { status: 'qualified-conflict'; conflict: NwiSourceAcreageConflict }
  | { status: 'invalid'; sourceFeatureId?: string; reason: string };

/** Preserve the native compiler's source conflict contract. Raw archive and cell
 * bytes are authenticated by refdata closure; this validates the consumer shape
 * and identity consistency, not a replacement native-WKB attestation. */
export function readNwiAcreageQualification(attrs: Record<string, unknown>): NwiAcreageQualification {
  const keys = ['sourceAcreageConflict', 'sourceAttributeVariants', 'sourceQualificationReceiptHash'];
  if (!keys.some(key => key in attrs)) return { status: 'ordinary' };
  const invalid = (): NwiAcreageQualification => ({ status: 'invalid',
    ...(nonempty(attrs.sourceFeatureId) ? { sourceFeatureId: attrs.sourceFeatureId } : {}),
    reason: 'The source acreage disagreement has incomplete or inconsistent qualification metadata. Acreage remains unavailable.' });
  const conflict = attrs.sourceAcreageConflict;
  const variants = attrs.sourceAttributeVariants;
  const receipt = attrs.sourceQualificationReceiptHash;
  const identity = attrs.sourceFeatureId;
  if (attrs.ACRES !== null || !nonempty(identity) || !nonempty(receipt) || !sha.test(receipt)
    || !object(conflict) || conflict.status !== 'unresolved-source-disagreement' || conflict.field !== 'ACRES'
    || !Array.isArray(variants) || variants.length < 2 || variants.length > 53
    || !Array.isArray(conflict.values) || conflict.values.length !== variants.length) return invalid();
  const occurrences = new Set<string>();
  const acreValues = new Set<number>();
  let science: string | undefined;
  let crs: string | undefined;
  let nativeHash: string | undefined;
  let nativeBytes: number | undefined;
  for (let i = 0; i < variants.length; i++) {
    const row: unknown = variants[i];
    const declared: unknown = conflict.values[i];
    if (!object(row) || !object(row.properties) || !object(row.archive) || !object(declared)
      || !nonempty(row.sourceCode) || !/^[A-Za-z]{2,8}$/.test(row.sourceCode)
      || !Number.isSafeInteger(row.sourceFid) || (row.sourceFid as number) <= 0
      || !nonempty(row.fidColumn) || row.properties[row.fidColumn] !== row.sourceFid
      || row.sourceFeatureId !== identity || !nonempty(row.sourceLayer) || row.sourceLayer.toLowerCase() !== `${row.sourceCode}_wetlands`.toLowerCase()
      || !nonempty(row.sourceSrs) || !nonempty(row.geometryHash) || !sha.test(row.geometryHash)
      || !nonempty(row.propertiesHash) || !sha.test(row.propertiesHash)
      || !nonempty(row.nativeWkbSha256) || !sha.test(row.nativeWkbSha256)
      || !Number.isSafeInteger(row.nativeWkbBytes) || (row.nativeWkbBytes as number) <= 0 || (row.nativeWkbBytes as number) > 32 * 1024 * 1024
      || !nonempty(row.archive.path) || !/^gs:\/\/[^/]+\/refdata\/nwi-wetlands\//.test(row.archive.path)
      || !row.archive.path.includes(`/raw/${row.sourceCode}/`)
      || !nonempty(row.archive.generation) || !/^\d+$/.test(row.archive.generation)
      || !nonempty(row.archive.sha256) || !sha.test(row.archive.sha256)
      || !Number.isSafeInteger(row.archive.sizeBytes) || (row.archive.sizeBytes as number) <= 0
      || !finiteAcres(row.properties.ACRES) || !nonempty(row.properties.NWI_ID)
      || !nonempty(row.properties.ATTRIBUTE) || !nonempty(row.properties.WETLAND_TYPE)
      || identity !== `${row.properties.NWI_ID}:${row.geometryHash}`
      || attrs.NWI_ID !== row.properties.NWI_ID || attrs.ATTRIBUTE !== row.properties.ATTRIBUTE
      || attrs.WETLAND_TYPE !== row.properties.WETLAND_TYPE
      || declared.sourceCode !== row.sourceCode || declared.sourceFid !== row.sourceFid
      || declared.value !== row.properties.ACRES || declared.propertiesHash !== row.propertiesHash) return invalid();
    const occurrence = `${row.sourceCode}:${row.sourceFid}`;
    if (occurrences.has(occurrence)) return invalid();
    occurrences.add(occurrence);
    acreValues.add(row.properties.ACRES);
    const scientificProperties = Object.fromEntries(Object.entries(row.properties).filter(([key]) => key !== row.fidColumn && key !== 'ACRES'));
    const currentScience = hashCanonicalJson(scientificProperties);
    if ((science !== undefined && science !== currentScience) || (crs !== undefined && crs !== row.sourceSrs)
      || (nativeHash !== undefined && nativeHash !== row.nativeWkbSha256)
      || (nativeBytes !== undefined && nativeBytes !== row.nativeWkbBytes)) return invalid();
    science = currentScience; crs = row.sourceSrs; nativeHash = row.nativeWkbSha256; nativeBytes = row.nativeWkbBytes as number;
  }
  if (acreValues.size < 2) return invalid();
  return { status: 'qualified-conflict', conflict: {
    sourceFeatureId: identity,
    sourceAcreageConflict: structuredClone(conflict) as unknown as NwiSourceAcreageConflict['sourceAcreageConflict'],
    sourceAttributeVariants: structuredClone(variants) as NwiSourceAttributeVariant[],
    sourceQualificationReceiptHash: receipt,
  } };
}
